import { matchEvaluationPlanPath } from '@app/routes/evaluationPlanBreadcrumb';
import { paths } from '@app/routes/paths';
import { useAppSelector } from '@app/store/hooks';
import { useAuth } from '@features/auth/hooks/useAuth';
import { Role } from '@features/auth/types';
import { doorsApi,evaluationDoorsListQuery  } from '@features/doors/doorsApi';
import { projectClientsApi } from '@features/projectClients/projectClientsApi';
import { projectsApi } from '@features/projects/projectsApi';
import { tDynamic } from '@i18n/helpers';
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useHotkey } from '@shared/hooks/useHotkey';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

export type CommandGroup = 'actions' | 'navigation' | 'customers' | 'projects' | 'doors';

const GROUP_ORDER: readonly CommandGroup[] = [
  'actions',
  'doors',
  'customers',
  'projects',
  'navigation',
];

const GROUP_LABEL_KEY: Record<CommandGroup, string> = {
  actions: 'palette.groups.actions',
  navigation: 'palette.groups.navigation',
  customers: 'palette.groups.customers',
  projects: 'palette.groups.projects',
  doors: 'palette.groups.doors',
};

export interface Command {
  id: string;
  /** Plain-text label used for fuzzy-matching and rendering. */
  label: string;
  /** Optional dimmer subtitle. */
  hint?: string;
  group: CommandGroup;
  /** Callback fired when the command is selected; palette closes after. */
  run: () => void;
  /** Optional role gate — command is filtered out if user lacks any role. */
  requiredRoles?: readonly Role[];
  icon?: ReactNode;
}

export interface CommandPaletteProps {
  /** Extra commands appended to the built-in navigation set. */
  commands?: readonly Command[];
}

const SEARCH_MIN_CHARS = 2;
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 3;

/** Flat render model: a subheader per group, commands in group order. */
export function buildPaletteSections(
  commands: readonly Command[],
): { group: CommandGroup; commands: Command[] }[] {
  return GROUP_ORDER.map((group) => ({
    group,
    commands: commands.filter((c) => c.group === group),
  })).filter((section) => section.commands.length > 0);
}

/**
 * Mountable Cmd/Ctrl+K palette: navigation, contextual actions and async
 * search across customers, projects and (on the plan page) the doors of
 * the current evaluation.
 *
 * Why a stub vs a heavier library:
 *   - Zero deps; reuses MUI primitives so theming & a11y come for free.
 *   - The matching is intentionally simple substring scoring; if richer
 *     fuzzy is needed later, swap in `fuse.js` without touching callers.
 */
export function CommandPalette({ commands = [] }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);

  useHotkey('mod+k', (event) => {
    event.preventDefault();
    setOpen((prev) => !prev);
  });

  const isTenant = hasRole([Role.Tenant], 'any');
  const planCtx = useMemo(() => matchEvaluationPlanPath(location.pathname), [location.pathname]);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(id);
    };
  }, [query]);

  // Async sources — fired only while open with a real query.
  const searchActive = open && isTenant && debouncedQuery.length >= SEARCH_MIN_CHARS;
  const [triggerClients, clientsResult] = projectClientsApi.useLazyListProjectClientsQuery();
  const [triggerProjects, projectsResult] = projectsApi.useLazyListProjectsQuery();

  useEffect(() => {
    if (!searchActive) return;
    void triggerClients(
      { page: 1, limit: SEARCH_LIMIT, search: debouncedQuery },
      /* preferCacheValue */ true,
    );
    void triggerProjects(
      { page: 1, limit: SEARCH_LIMIT, search: debouncedQuery },
      /* preferCacheValue */ true,
    );
  }, [searchActive, debouncedQuery, triggerClients, triggerProjects]);

  // Doors of the current evaluation — only available while the plan/offer
  // editor is mounted (evaluation door list is not kept in RTK cache).
  const doorsSelector = useMemo(
    () =>
      planCtx
        ? doorsApi.endpoints.listDoors.select(evaluationDoorsListQuery(planCtx.evaluationId))
        : null,
    [planCtx],
  );
  const doorsCacheEntry = useAppSelector((state) =>
    // The store's RootState predates the lazily injected doors endpoints,
    // so the selector's structural state type is wider than RootState.
    doorsSelector ? doorsSelector(state as never) : undefined,
  );

  const go = (to: string) => () => {
    // react-router v7 returns a Promise from `navigate`; we don't await it
    // because the palette closes synchronously and any nav errors bubble
    // through React Router's normal error UI.
    void navigate(to);
  };

  const baseCommands = useMemo<readonly Command[]>(
    () => [
      {
        id: 'action.newCustomer',
        label: t('palette.actions.newCustomer'),
        group: 'actions',
        icon: <PersonAddAltOutlinedIcon fontSize="small" />,
        run: go(`${paths.projectClients}?create=1`),
        requiredRoles: [Role.Tenant],
      },
      ...(planCtx
        ? ([
            {
              id: 'action.addDoor',
              label: t('palette.actions.addDoor'),
              group: 'actions',
              icon: <AddLocationAltOutlinedIcon fontSize="small" />,
              run: go(`${location.pathname}?addDoor=1`),
              requiredRoles: [Role.Tenant],
            },
            {
              id: 'action.startReview',
              label: t('palette.actions.startReview'),
              group: 'actions',
              icon: <PlaylistAddCheckIcon fontSize="small" />,
              run: go(`${location.pathname}?review=1`),
              requiredRoles: [Role.Tenant],
            },
          ] satisfies Command[])
        : []),
      {
        id: 'nav.dashboard',
        label: tDynamic(t, 'nav.dashboard'),
        group: 'navigation',
        run: go(paths.dashboard),
        requiredRoles: [Role.Admin],
      },
      { id: 'nav.profile', label: tDynamic(t, 'nav.profile'), group: 'navigation', run: go(paths.profile) },
      {
        id: 'nav.users',
        label: tDynamic(t, 'nav.users'),
        group: 'navigation',
        run: go(paths.users),
        requiredRoles: [Role.Admin],
      },
      {
        id: 'nav.clients.admin',
        label: tDynamic(t, 'nav.clients'),
        group: 'navigation',
        run: go(paths.clients),
        requiredRoles: [Role.Admin],
      },
      {
        id: 'nav.clients.tenant',
        label: tDynamic(t, 'nav.clients'),
        group: 'navigation',
        run: go(paths.projectClients),
        requiredRoles: [Role.Tenant],
      },
      {
        id: 'nav.doorTemplates',
        label: tDynamic(t, 'nav.doorTemplates'),
        group: 'navigation',
        run: go(paths.doorTemplates),
        requiredRoles: [Role.Tenant],
      },
      {
        id: 'nav.doorRules',
        label: tDynamic(t, 'nav.doorRules'),
        group: 'navigation',
        run: go(paths.doorRules),
        requiredRoles: [Role.Tenant],
      },
      {
        id: 'nav.partners',
        label: tDynamic(t, 'nav.partners'),
        group: 'navigation',
        run: go(paths.partners),
        requiredRoles: [Role.Admin],
      },
    ],
    // `go` closes over `navigate`; reset commands when it changes (or on
    // language switch so labels stay localized).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, t, planCtx, location.pathname],
  );

  const searchCommands = useMemo<readonly Command[]>(() => {
    const result: Command[] = [];
    const q = debouncedQuery.toLowerCase();

    if (planCtx && q.length >= 1) {
      const doors = doorsCacheEntry?.data?.data ?? [];
      for (const d of doors) {
        const label = d.externalDoorId;
        const hint = d.doorLabel ?? undefined;
        if (
          !label.toLowerCase().includes(q) &&
          !(hint?.toLowerCase().includes(q) ?? false)
        ) {
          continue;
        }
        result.push({
          id: `door.${d.id}`,
          label,
          ...(hint !== undefined ? { hint } : {}),
          group: 'doors',
          icon: <MeetingRoomOutlinedIcon fontSize="small" />,
          run: go(`${location.pathname}?door=${encodeURIComponent(d.id)}`),
        });
        if (result.length >= SEARCH_LIMIT) break;
      }
    }

    if (searchActive) {
      for (const c of clientsResult.data?.data ?? []) {
        result.push({
          id: `customer.${c.id}`,
          label: c.name,
          ...(c.contactName ? { hint: c.contactName } : {}),
          group: 'customers',
          run: go(paths.projectClientProjects.replace(':projectClientId', c.id)),
        });
      }
      for (const p of projectsResult.data?.data ?? []) {
        result.push({
          id: `project.${p.id}`,
          label: p.name,
          ...(p.projectCode ? { hint: p.projectCode } : {}),
          group: 'projects',
          run: go(
            paths.projectWorkspace
              .replace(':projectClientId', p.projectClientId)
              .replace(':projectId', p.id),
          ),
        });
      }
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedQuery,
    searchActive,
    planCtx,
    doorsCacheEntry,
    clientsResult.data,
    projectsResult.data,
    location.pathname,
    navigate,
  ]);

  const all = useMemo(
    () =>
      [...baseCommands, ...commands, ...searchCommands].filter(
        (c) => !c.requiredRoles || hasRole(c.requiredRoles, 'any'),
      ),
    [baseCommands, commands, searchCommands, hasRole],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Search results match by construction; static commands filter by label.
    const matches = q
      ? all.filter(
          (c) =>
            c.group === 'customers' ||
            c.group === 'projects' ||
            c.group === 'doors' ||
            c.label.toLowerCase().includes(q) ||
            (c.hint?.toLowerCase().includes(q) ?? false),
        )
      : all;
    return buildPaletteSections(matches).flatMap((s) => s.commands);
  }, [all, query]);

  const sections = useMemo(() => buildPaletteSections(filtered), [filtered]);

  const close = () => {
    setOpen(false);
    setQuery('');
    setDebouncedQuery('');
    setHighlighted(0);
  };

  const runCommand = (cmd: Command) => {
    cmd.run();
    close();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = filtered[highlighted];
      if (cmd) runCommand(cmd);
    }
  };

  const isSearching =
    searchActive && (clientsResult.isFetching || projectsResult.isFetching);

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { sx: { mt: '15vh' } } }}
    >
      <Box sx={{ p: 2 }} onKeyDown={onKeyDown}>
        <TextField
          autoFocus
          fullWidth
          placeholder={t('palette.placeholder')}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlighted(0);
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="caption" color="text.secondary">
                    {isSearching ? t('palette.loading') : t('palette.shortcut')}
                  </Typography>
                </InputAdornment>
              ),
            },
          }}
        />
        {filtered.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('palette.empty')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ mt: 1, maxHeight: 360, overflow: 'auto' }} role="listbox" disablePadding>
            {sections.map((section) => (
              <Box key={section.group}>
                <ListSubheader disableSticky sx={{ lineHeight: 2.2, bgcolor: 'transparent' }}>
                  {tDynamic(t, GROUP_LABEL_KEY[section.group])}
                </ListSubheader>
                {section.commands.map((cmd) => {
                  const flatIndex = filtered.indexOf(cmd);
                  return (
                    <ListItemButton
                      key={cmd.id}
                      selected={flatIndex === highlighted}
                      onMouseEnter={() => {
                        setHighlighted(flatIndex);
                      }}
                      onClick={() => {
                        runCommand(cmd);
                      }}
                    >
                      {cmd.icon && <Box sx={{ mr: 1.5, display: 'flex' }}>{cmd.icon}</Box>}
                      <ListItemText primary={cmd.label} secondary={cmd.hint} />
                    </ListItemButton>
                  );
                })}
              </Box>
            ))}
          </List>
        )}
      </Box>
    </Dialog>
  );
}
